// src/host/hooks/useSocketConnection.ts

import { useEffect, useRef, useState } from 'react';
import { GlobalSocketService } from '@host/services/socket.service';
import { useGuestStore, useUserProfileStore } from '../stores';
import { Logger } from '../../shared/services/logger.service';
import { useMultiFloorStateStore } from '../../apps/office/stores/MultiFloorStore';
import { OfficeService } from '../../apps/office/services/OfficeService';

interface ConnectionResult {
  isConnected: boolean;
  isInitialized: boolean;
  connectionStatus: any;
  serviceStats: any;
  error?: Error;
}

export function useSocketConnection(): ConnectionResult {
  const [connectionResult, setConnectionResult] = useState<ConnectionResult>({
    isConnected: false,
    isInitialized: false,
    connectionStatus: GlobalSocketService.getConnectionStatus('/events'),
    serviceStats: GlobalSocketService.getServiceStats(),
  });

  const userPid = useUserProfileStore((user) => user.profile?.pid);
  const guestPid = useGuestStore((guest) => guest.guest?.pid);

  const isInitialized = useRef(false);
  const healthCheckTimer = useRef<number>();
  const handlerRegistered = useRef(false); // Track if handler is registered

  // 🔌 Initial Connection
  useEffect(() => {
    if (isInitialized.current) return;
    if (!userPid && !guestPid) return;

    Logger.debug(`[useSocketConnection] 🚀 Initializing socket connection...`);

    const initialize = async () => {
      try {
        const result = await GlobalSocketService.initializeConnection();

        setConnectionResult((prev) => ({
          ...prev,
          isConnected: result.success,
          isInitialized: true,
          connectionStatus: result.connectionStatus,
          error: result.error,
        }));

        isInitialized.current = true;
      } catch (error) {
        console.error(`[useSocketConnection] ❌ Initialization failed:`, error);
        setConnectionResult((prev) => ({
          ...prev,
          isConnected: false,
          isInitialized: true,
          error: error as Error,
        }));
      }
    };

    initialize();
  }, [userPid, guestPid]); // ✅ Empty dependency array for initialization

  // 🎯 Event Handler Registration - DISABLED (EventService handles this)
  useEffect(() => {
    if (
      connectionResult.isConnected &&
      connectionResult.isInitialized &&
      !handlerRegistered.current
    ) {
      Logger.debug(
        `[useSocketConnection] 🎯 Socket connected - EventService already handles event routing`
      );
      handlerRegistered.current = true;

      // 🔄 Start checking for user context availability more frequently after connection
      const checkUserContext = async () => {
        try {
          // Use the regular check method first
          await GlobalSocketService.checkAndInitializeSubscriptions();
        } catch (error) {
          console.error(`[useSocketConnection] ❌ Failed to check subscriptions:`, error);
        }
      };

      // Check immediately
      checkUserContext();

      // Then check every 2 seconds for the first 30 seconds after connection
      let attempts = 0;
      const maxAttempts = 15; // 30 seconds / 2 seconds = 15 attempts
      const contextCheckInterval = setInterval(async () => {
        attempts++;
        await checkUserContext();

        // Stop checking after max attempts or if subscriptions are initialized
        if (
          attempts >= maxAttempts ||
          GlobalSocketService.getServiceStats().connections.total > 0
        ) {
          clearInterval(contextCheckInterval);
          Logger.debug(
            `[useSocketConnection] ✅ Stopped user context checking after ${attempts} attempts`
          );
        }
      }, 2000);

      // Cleanup function to clear interval if component unmounts
      return () => {
        clearInterval(contextCheckInterval);
      };
    }
  }, [connectionResult.isConnected, connectionResult.isInitialized]);

  // 🔄 Reconnection on Page Visibility
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isInitialized.current) {
        Logger.debug(`[useSocketConnection] 👁️ Page visible, ensuring connection...`);

        const isHealthy = await GlobalSocketService.ensureConnection('/events');

        setConnectionResult((prev) => ({
          ...prev,
          isConnected: isHealthy,
          connectionStatus: GlobalSocketService.getConnectionStatus('/events'),
        }));

        // EventService handles event routing automatically
        if (isHealthy && handlerRegistered.current) {
          Logger.debug(
            `[useSocketConnection] 🔄 Connection re-established - EventService handles event routing`
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 🏥 Periodic Health Checks
  useEffect(() => {
    const config = GlobalSocketService.getConnectionConfig();
    const healthCheckInterval = config.connection.healthCheckInterval;

    if (!healthCheckInterval || healthCheckInterval <= 0) return;

    Logger.debug(
      `[useSocketConnection] 🏥 Setting up health checks every ${healthCheckInterval / 1000} seconds`
    );

    const performHealthCheck = async () => {
      if (!isInitialized.current) return;

      try {
        // Just check connection status - don't force reconnection
        const status = GlobalSocketService.getConnectionStatus('/events');

        setConnectionResult((prev) => ({
          ...prev,
          isConnected: status.connected,
          connectionStatus: status,
          serviceStats: GlobalSocketService.getServiceStats(),
        }));

        // Only try to ensure connection if explicitly disconnected
        if (!status.connected) {
          Logger.debug(
            `[useSocketConnection] 🔄 Health check detected disconnection, attempting reconnection...`
          );
          const isHealthy = await GlobalSocketService.ensureConnection('/events');

          setConnectionResult((prev) => ({
            ...prev,
            isConnected: isHealthy,
            connectionStatus: GlobalSocketService.getConnectionStatus('/events'),
          }));
        }

        // Check if subscriptions need to be initialized (in case user context became available)
        await GlobalSocketService.checkAndInitializeSubscriptions();

        // Re-fetch floor presence as a fallback to self-heal any avatar placement drift
        // that may have occurred due to missed WebSocket events during the interval.
        const currentFloorPid = useMultiFloorStateStore.getState().currentFloorPresence;
        if (currentFloorPid && status.connected) {
          OfficeService.getInstance()
            .fetchFloorPresence(currentFloorPid)
            .catch((err) => Logger.warn('[SocketHealth] Floor presence refresh failed:', err));
        }
      } catch (error) {
        console.error(`[useSocketConnection] ❌ Health check failed:`, error);
      }
    };

    healthCheckTimer.current = setInterval(performHealthCheck, healthCheckInterval);

    return () => {
      if (healthCheckTimer.current) {
        clearInterval(healthCheckTimer.current);
      }
    };
  }, []);

  // 🧹 Cleanup
  useEffect(() => {
    return () => {
      if (healthCheckTimer.current) {
        clearInterval(healthCheckTimer.current);
      }

      // Reset handler registration on cleanup
      handlerRegistered.current = false;
    };
  }, []);

  return connectionResult;
}
